// TribeX Productivity - one brand record per domain.
//
// The record (name, logo, favicon, theme colours, footer, support address) lives in the identity service for the
// domain (identity.<domain>/api/branding, edited by a super admin). Every app reads it at start-up, so changing the
// record re-brands identity and all the tools for that domain without a rebuild. The values below are only the
// fallback shown before the first answer, or when identity cannot be reached.

import { useSyncExternalStore } from "react";

export interface BrandConfig {
    /** Product family name: "TribeX" in "TribeX Notes". */
    name: string;
    tagline: string;
    /** Image shown in place of the text wordmark (absolute URL or a path on the identity host). Empty = text only. */
    logoUrl: string;
    /** Browser-tab icon. Empty = keep the app's own icon. */
    faviconUrl: string;
    /** Accent in the light theme (#rrggbb). */
    primary: string;
    /** Accent in the dark theme (#rrggbb). */
    primaryDark: string;
    footerText: string;
    supportEmail: string;
}

export const DEFAULT_BRAND: BrandConfig = {
    name: "TribeX",
    tagline: "",
    logoUrl: "",
    faviconUrl: "",
    primary: "#1F4FD8",
    primaryDark: "#7FA2FF",
    footerText: "",
    supportEmail: ""
};

const CACHE_KEY = "tx.brand.v1";
const STYLE_ID = "tx-brand-style";
const hasWindow = typeof window !== "undefined" && typeof document !== "undefined";

let current: BrandConfig = DEFAULT_BRAND;
const listeners = new Set<() => void>();
let started = false;
let titleObserver: MutationObserver | null = null;

function clean(raw: unknown): BrandConfig | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    const str = (k: keyof BrandConfig, max = 200) => (typeof r[k] === "string" ? (r[k] as string).trim().slice(0, max) : "");
    const hex = (k: "primary" | "primaryDark") => (/^#[0-9a-fA-F]{6}$/.test(str(k, 7)) ? str(k, 7) : DEFAULT_BRAND[k]);
    const name = str("name", 60);
    if (!name) return null;
    return {
        name,
        tagline: str("tagline"),
        logoUrl: str("logoUrl", 500),
        faviconUrl: str("faviconUrl", 500),
        primary: hex("primary"),
        primaryDark: hex("primaryDark"),
        footerText: str("footerText"),
        supportEmail: str("supportEmail")
    };
}

function hexToHslTriplet(hex: string): { triplet: string; light: number } {
    const n = parseInt(hex.slice(1), 16);
    const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    let h = 0, s = 0;
    if (d > 0) {
        s = d / (1 - Math.abs(2 * l - 1));
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h = Math.round(h * 60);
        if (h < 0) h += 360;
    }
    return { triplet: `${h} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`, light: l };
}

function applyColours(b: BrandConfig) {
    const light = hexToHslTriplet(b.primary);
    const dark = hexToHslTriplet(b.primaryDark);
    const fgLight = light.light > 0.6 ? "222 47% 11%" : "0 0% 100%";
    // Only while the person has not picked another accent in Appearance (that sets data-palette).
    const css =
        `html:not([data-palette]){--primary:${light.triplet};--ring:${light.triplet};--primary-foreground:${fgLight};` +
        `--sidebar-primary:${light.triplet};--sidebar-ring:${light.triplet}}` +
        `html.dark:not([data-palette]){--primary:${dark.triplet};--ring:${dark.triplet};--primary-foreground:222 47% 11%;` +
        `--sidebar-primary:${dark.triplet};--sidebar-ring:${dark.triplet}}`;
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
        el = document.createElement("style");
        el.id = STYLE_ID;
        document.head.appendChild(el);
    }
    if (el.textContent !== css) el.textContent = css;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta && !document.documentElement.hasAttribute("data-palette")) meta.content = b.primary;
}

function brandedTitle(title: string, b: BrandConfig) {
    return b.name === DEFAULT_BRAND.name ? title : title.split(DEFAULT_BRAND.name).join(b.name);
}

function applyTitle(b: BrandConfig) {
    const next = brandedTitle(document.title, b);
    if (next !== document.title) document.title = next;
    if (titleObserver || b.name === DEFAULT_BRAND.name) return;
    const el = document.querySelector("title");
    if (!el) return;
    // Pages set their own titles ("TribeX Notes - file"); keep mapping them to the configured name.
    titleObserver = new MutationObserver(() => {
        const t = brandedTitle(document.title, current);
        if (t !== document.title) document.title = t;
    });
    titleObserver.observe(el, { childList: true, characterData: true, subtree: true });
}

function applyFavicon(b: BrandConfig) {
    if (!b.faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== b.faviconUrl) link.setAttribute("href", b.faviconUrl);
}

function apply(b: BrandConfig) {
    current = b;
    if (hasWindow) {
        applyColours(b);
        applyTitle(b);
        applyFavicon(b);
    }
    for (const l of listeners) l();
}

/** The identity host for this domain: identity.<parent domain>, unless the build names one. */
export function brandBaseUrl(): string {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    const fixed = env?.VITE_IDENTITY_AUTHORITY?.trim();
    const host = hasWindow ? window.location.hostname : "";
    const labels = host.split(".");
    const derived = labels.length >= 3 ? `https://identity.${labels.slice(1).join(".")}` : "";
    // A build for a different domain than the one it is opened on (a test host) follows the page's own domain.
    return (derived || fixed || "").replace(/\/+$/, "");
}

/** Absolute URL of the logo / favicon, which may be given relative to the identity host. */
export function brandAssetUrl(path: string): string {
    if (!path) return "";
    if (/^(https?:|data:)/i.test(path)) return path;
    return brandBaseUrl() + (path.startsWith("/") ? path : `/${path}`);
}

function resolve(b: BrandConfig): BrandConfig {
    return { ...b, logoUrl: brandAssetUrl(b.logoUrl), faviconUrl: brandAssetUrl(b.faviconUrl) };
}

/** Call once at start-up. Applies the cached record at once, then refreshes it from identity. */
export function initBrand() {
    if (!hasWindow || started) return;
    started = true;
    try {
        const cached = clean(JSON.parse(window.localStorage.getItem(CACHE_KEY) ?? "null"));
        apply(cached ? resolve(cached) : DEFAULT_BRAND);
    } catch {
        apply(DEFAULT_BRAND);
    }
    const base = brandBaseUrl();
    if (!base) return;
    void fetch(`${base}/api/branding`, { cache: "no-cache", credentials: "omit" })
        .then((r) => (r.ok ? r.json() : null))
        .then((raw) => {
            const b = clean(raw);
            if (!b) return;
            try {
                window.localStorage.setItem(CACHE_KEY, JSON.stringify(b));
            } catch {
                /* storage can be blocked */
            }
            apply(resolve(b));
        })
        .catch(() => undefined);
}

export function getBrand(): BrandConfig {
    return current;
}

export function subscribeBrand(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useBrand(): BrandConfig {
    return useSyncExternalStore(subscribeBrand, getBrand, getBrand);
}
